import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Overline, Spinner, Empty, Pill, Gauge } from "@/components/Widgets";
import { MagnifyingGlass, Sparkle, Rocket, Stack, Broadcast, Newspaper, ArrowSquareOut, Lightning, CheckCircle, Globe, PaperPlaneTilt, LinkSimpleBreak } from "@phosphor-icons/react";
import { PageGuide } from "@/components/PageGuide";

export default function SeoEngine() {
  const [form, setForm] = useState({ brand: "", niche: "", keywords: "" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [target, setTarget] = useState(30);
  const [campBusy, setCampBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [wp, setWp] = useState({ connected: false });
  const [wpForm, setWpForm] = useState({ site_url: "", username: "", application_password: "" });
  const [wpBusy, setWpBusy] = useState(false);
  const [pubId, setPubId] = useState(null);

  const loadWp = () => api.get("/integrations/wordpress").then(({ data }) => setWp(data)).catch(() => {});
  useEffect(() => {
    api.get("/seo/campaign").then(({ data }) => data && setCampaign(data)).catch(() => {});
    loadWp();
  }, []);

  const connectWp = async (e) => {
    e.preventDefault();
    if (!wpForm.site_url.startsWith("https://")) return toast.error("Site URL must start with https://");
    setWpBusy(true);
    try {
      await api.post("/integrations/wordpress", wpForm);
      setWpForm({ site_url: "", username: "", application_password: "" });
      loadWp();
      toast.success("WordPress connected");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setWpBusy(false); }
  };

  const disconnectWp = async () => {
    await api.delete("/integrations/wordpress");
    loadWp();
    toast.success("WordPress disconnected");
  };

  const publishWp = async (articleId) => {
    setPubId(articleId);
    try {
      const { data } = await api.post(`/articles/${articleId}/publish-wordpress`);
      toast.success("Published to WordPress", { action: data.link ? { label: "View", onClick: () => window.open(data.link, "_blank") } : undefined });
      setCampaign((c) => c && { ...c, articles: c.articles.map((a) => a.id === articleId ? { ...a, wp_link: data.link } : a) });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setPubId(null); }
  };

  const analyze = async (e) => {
    e.preventDefault();
    if (!form.brand.trim()) return toast.error("Enter brand or name");
    setBusy(true);
    try {
      const { data } = await api.post("/seo/analyze", form);
      setData(data);
      toast.success("SEO strategy generated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const launchCampaign = async () => {
    if (!form.brand.trim()) return toast.error("Enter a brand/name first");
    setCampBusy(true);
    try {
      const { data } = await api.post("/seo/campaign", { ...form, monthly_target: target });
      setCampaign(data);
      toast.success(`Campaign launched · ${data.stats.total} articles planned`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCampBusy(false);
    }
  };

  const generateNext = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/seo/campaign/${campaign.id}/generate?count=4`);
      setCampaign(data);
      toast.success("Articles generated & published to the blog");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setGenBusy(false);
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <PageGuide title="How the SEO Engine works" steps={[
        "Set a monthly article target (20–50) and click Launch campaign to plan every article.",
        "Click 'Generate & publish next 4' to auto-write articles with covers to your public blog.",
        "Build a full SEO strategy on the right for keywords, pillars and a distribution plan.",
        "Connect WordPress to also push articles to your own external blog.",
      ]} />
      <SearchEngineCard />
      {/* Aggressive Article Engine */}
      <div className="relative p-[1px] tracing-border">
        <Card className="bg-card" testid="article-engine">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Newspaper size={30} weight="fill" className="text-primary shrink-0 mt-1" />
              <div>
                <h3 className="font-head font-bold text-lg">Aggressive Article Engine</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Auto-generate 20–50 SEO articles/month and publish them to your live <a href="/blog" target="_blank" rel="noreferrer" className="text-primary underline">public blog</a> — flooding the web with positive content.
                </p>
              </div>
            </div>
            {!campaign && (
              <div className="flex items-end gap-3">
                <div>
                  <label className="overline text-muted-foreground block mb-2">Monthly target</label>
                  <input type="range" min="20" max="50" value={target} data-testid="article-target"
                    onChange={(e) => setTarget(Number(e.target.value))} className="w-40 accent-[#E9AC0C]" />
                  <div className="font-mono text-sm mt-1">{target} articles / month</div>
                </div>
                <button onClick={launchCampaign} disabled={campBusy} data-testid="launch-campaign-btn"
                  className="bg-primary text-primary-foreground font-semibold font-semibold px-5 py-3 text-sm flex items-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 whitespace-nowrap">
                  {campBusy ? <Spinner label="Planning…" /> : (<><Rocket size={18} weight="fill" /> Launch campaign</>)}
                </button>
              </div>
            )}
          </div>

          {campaign && (
            <div className="mt-5 pt-5 border-t border-border" data-testid="campaign-panel">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                  <Overline className="text-muted-foreground">CAMPAIGN · {campaign.brand}</Overline>
                  <div className="text-sm mt-1">
                    <span className="font-mono text-emerald-400 font-bold" data-testid="published-count">{campaign.stats.published}</span>
                    <span className="text-muted-foreground"> published · </span>
                    <span className="font-mono text-primary font-bold">{campaign.stats.planned}</span>
                    <span className="text-muted-foreground"> queued · target {campaign.monthly_target}/mo</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a href="/blog" target="_blank" rel="noreferrer"
                    className="border border-border px-3 py-2 text-sm hover:bg-secondary transition-colors duration-200 flex items-center gap-1">
                    View blog <ArrowSquareOut size={14} />
                  </a>
                  {campaign.stats.planned > 0 && (
                    <button onClick={generateNext} disabled={genBusy} data-testid="generate-articles-btn"
                      className="bg-primary text-primary-foreground font-semibold font-semibold px-4 py-2 text-sm flex items-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
                      {genBusy ? <Spinner label="Writing & publishing…" /> : (<><Lightning size={16} weight="fill" /> Generate & publish next 4</>)}
                    </button>
                  )}
                </div>
              </div>
              <div className="h-2 bg-secondary overflow-hidden mb-4">
                <div className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${Math.round((campaign.stats.published / Math.max(campaign.stats.total, 1)) * 100)}%` }} data-testid="campaign-progress" />
              </div>
              <div className="grid sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1">
                {campaign.articles.map((a) => (
                  <div key={a.id} data-testid={`article-${a.id}`}
                    className="border border-border p-3 flex items-start gap-2">
                    {a.status === "published"
                      ? <CheckCircle size={18} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
                      : <span className="h-4 w-4 border border-muted-foreground rounded-full shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.title}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Pill tone={a.status === "published" ? "positive" : "neutral"}>{a.status}</Pill>
                        <span className="overline text-muted-foreground truncate">{a.platform}</span>
                        {a.status === "published" && a.slug && (
                          <a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer" className="text-primary text-xs underline">read</a>
                        )}
                        {a.status === "published" && wp.connected && (
                          a.wp_link ? (
                            <a href={a.wp_link} target="_blank" rel="noreferrer" className="text-emerald-400 text-xs underline flex items-center gap-0.5"><CheckCircle size={11} weight="fill" /> on WP</a>
                          ) : (
                            <button onClick={() => publishWp(a.id)} disabled={pubId === a.id} data-testid={`publish-wp-${a.id}`}
                              className="text-xs flex items-center gap-0.5 text-muted-foreground hover:text-primary transition-colors duration-200 disabled:opacity-50">
                              <PaperPlaneTilt size={12} weight="bold" /> {pubId === a.id ? "Publishing…" : "Push to WP"}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* WordPress publishing */}
      <Card testid="wordpress-card">
        <div className="flex items-center gap-2 mb-3">
          <Globe size={22} weight="bold" className="text-primary" />
          <h3 className="font-head font-bold text-lg">WordPress Publishing</h3>
          {wp.connected && <span className="ml-auto text-emerald-400 font-mono text-xs flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Connected</span>}
        </div>
        {wp.connected ? (
          <div className="flex flex-wrap items-center justify-between gap-3" data-testid="wp-connected">
            <div className="text-sm">
              <span className="text-muted-foreground">Site: </span><span className="font-mono">{wp.site_url}</span>
              <span className="text-muted-foreground"> · user </span><span className="font-mono">{wp.username}</span>
            </div>
            <button onClick={disconnectWp} data-testid="wp-disconnect-btn"
              className="border border-border px-3 py-2 text-sm flex items-center gap-1 hover:text-accent hover:border-accent/40 transition-colors duration-200">
              <LinkSimpleBreak size={14} /> Disconnect
            </button>
          </div>
        ) : (
          <form onSubmit={connectWp} className="grid md:grid-cols-4 gap-2 items-end" data-testid="wp-connect-form">
            <div className="md:col-span-2">
              <label className="overline text-muted-foreground block mb-1">Site URL (https)</label>
              <input value={wpForm.site_url} data-testid="wp-site" onChange={(e) => setWpForm({ ...wpForm, site_url: e.target.value })}
                placeholder="https://yourblog.com" className="w-full bg-secondary border border-border px-3 py-2 text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1">Username</label>
              <input value={wpForm.username} data-testid="wp-username" onChange={(e) => setWpForm({ ...wpForm, username: e.target.value })}
                className="w-full bg-secondary border border-border px-3 py-2 text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1">App Password</label>
              <input type="password" value={wpForm.application_password} data-testid="wp-password" onChange={(e) => setWpForm({ ...wpForm, application_password: e.target.value })}
                className="w-full bg-secondary border border-border px-3 py-2 text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
            </div>
            <button type="submit" disabled={wpBusy} data-testid="wp-connect-btn"
              className="md:col-span-4 bg-primary text-primary-foreground font-semibold font-semibold py-2.5 text-sm hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
              {wpBusy ? "Connecting…" : "Connect WordPress"}
            </button>
            <p className="md:col-span-4 text-xs text-muted-foreground">Create an Application Password in WordPress → Users → Profile. Credentials are encrypted and only used server-side.</p>
          </form>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 h-fit" testid="seo-form-card">
          <div className="flex items-center gap-2 mb-4">
            <MagnifyingGlass size={22} weight="bold" className="text-primary" />
            <h3 className="font-head font-bold text-lg">SEO Domination</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-5">Build a 24/7 content-distribution strategy to flood search & social with positive authority.</p>
          <form onSubmit={analyze} className="space-y-4">
            <Input label="Brand / Name" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} placeholder="Jordan Vega" testid="seo-brand" />
            <Input label="Niche" value={form.niche} onChange={(v) => setForm({ ...form, niche: v })} placeholder="Fashion & lifestyle" testid="seo-niche" />
            <Input label="Focus keywords (optional)" value={form.keywords} onChange={(v) => setForm({ ...form, keywords: v })} placeholder="sustainable fashion, style tips" testid="seo-keywords-input" />
            <button type="submit" disabled={busy} data-testid="analyze-seo-btn"
              className="w-full bg-primary text-primary-foreground font-semibold font-semibold py-3 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
              {busy ? <Spinner label="Analyzing…" /> : (<><Sparkle size={18} weight="fill" /> Build SEO strategy</>)}
            </button>
          </form>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          {!data && !busy && <Empty>Generate a full SEO + distribution playbook to dominate your search results.</Empty>}
          {busy && !data && <Card><div className="py-12 flex justify-center"><Spinner label="Mapping keyword landscape…" /></div></Card>}
          {data && (
            <>
              <Card testid="seo-health" className="flex items-center gap-6">
                <Gauge score={data.overall_seo_health} size={130} />
                <div>
                  <Overline className="text-accent">SEO HEALTH SCORE</Overline>
                  <p className="text-sm text-muted-foreground mt-2">Overall discoverability & authority potential for {form.brand}.</p>
                </div>
              </Card>

              <Card testid="primary-keywords">
                <Overline className="mb-3">PRIMARY KEYWORDS</Overline>
                <div className="divide-y divide-border">
                  {data.primary_keywords?.map((k, i) => (
                    <div key={i} className="py-2.5 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium font-mono">{k.keyword}</div>
                        <div className="text-xs text-muted-foreground">{k.opportunity}</div>
                      </div>
                      <Pill tone={k.difficulty}>{k.difficulty} diff</Pill>
                    </div>
                  ))}
                </div>
              </Card>

              <div className="grid md:grid-cols-2 gap-4">
                <Card testid="content-pillars">
                  <Overline className="flex items-center gap-1 mb-3"><Stack size={12} weight="fill" /> CONTENT PILLARS</Overline>
                  <div className="space-y-2">
                    {data.content_pillars?.map((p, i) => (
                      <div key={i} className="text-sm border border-border px-3 py-2">{p}</div>
                    ))}
                  </div>
                </Card>
                <Card testid="quick-wins">
                  <Overline className="flex items-center gap-1 mb-3 text-emerald-400"><Rocket size={12} weight="fill" /> QUICK WINS</Overline>
                  <ul className="space-y-2">
                    {data.quick_wins?.map((q, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-emerald-400">▪</span>{q}</li>
                    ))}
                  </ul>
                </Card>
              </div>

              <Card testid="content-ideas">
                <Overline className="mb-3">CONTENT IDEAS</Overline>
                <div className="grid sm:grid-cols-2 gap-3">
                  {data.content_ideas?.map((c, i) => (
                    <div key={i} className="border border-border p-3">
                      <div className="text-sm font-medium mb-2">{c.title}</div>
                      <div className="flex gap-2"><Pill>{c.platform}</Pill><Pill tone="neutral">{c.format}</Pill></div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card testid="distribution-plan">
                <Overline className="flex items-center gap-1 mb-3"><Broadcast size={12} weight="fill" /> 24/7 DISTRIBUTION PLAN</Overline>
                <div className="divide-y divide-border">
                  {data.distribution_plan?.map((d, i) => (
                    <div key={i} className="py-2.5 flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium">{d.channel}</div>
                        <div className="text-xs text-muted-foreground">{d.tactic}</div>
                      </div>
                      <span className="font-mono text-xs text-primary whitespace-nowrap">{d.cadence}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SearchEngineCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/seo/search-engine-status").then(({ data }) => setStatus(data)).catch(() => {}); }, []);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/seo/index-now");
      toast.success(`Submitted ${data.submitted} page(s) to ${data.engines.join(", ")}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };
  return (
    <Card testid="search-engine-card">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <Globe size={30} weight="fill" className="text-primary shrink-0" />
          <div>
            <h3 className="font-head font-bold text-lg">Publish to the WWW Search Engines</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Your articles are live web pages in your sitemap (Google crawls them automatically). Click below to <strong>instantly notify</strong> Bing, Yandex, Seznam &amp; Naver via IndexNow.
              {status && <span className="text-emerald-400"> · {status.published_articles} article(s) published{status.public_profile ? " + public profile" : ""}</span>}
            </p>
          </div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="index-now-btn"
          className="bg-primary text-primary-foreground font-semibold px-5 py-3 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 whitespace-nowrap">
          {busy ? <Spinner label="Submitting…" /> : (<><PaperPlaneTilt size={16} weight="fill" /> Submit to search engines</>)}
        </button>
      </div>
    </Card>
  );
}

function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="overline text-muted-foreground block mb-2">{label}</label>
      <input value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
    </div>
  );
}
